import { HeatingCircuit, IOExtension, ObjectArrays, Pump, } from 'z3k-config';
import { genIdAndIncCounter } from 'z3k-parser/lib/auxiliary';
import { heating_modes_ids } from '../constants';
import { set, setIn } from 'immutable';
import * as lodash from 'lodash';
import { getDeviceSpecV2 } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { composePhysicalIO } from 'z3k-parser/lib/input-outputs/utils';
export const setupIOExtensions = (input_z3k_config, blackList) => {
    const spec = getDeviceSpecV2(input_z3k_config?.device?.name, input_z3k_config?.device?.hardware);
    if (spec != null && !spec.io.io_extensions_supported) {
        return IOExtension.get.remove(input_z3k_config, IOExtension.isCE2);
    }
    const io_extensions_CE_2 = IOExtension.get.where(input_z3k_config, (io) => io.related_list?.length == 0 &&
        IOExtension.isCE2(io) &&
        !blackList.includes(io.id));
    if (io_extensions_CE_2.length === 0) {
        return input_z3k_config;
    }
    let z3k_config = { ...input_z3k_config };
    io_extensions_CE_2.forEach((io) => {
        const use_in_object_arrays = ObjectArrays.get.some(input_z3k_config, (array) => array.objects_links.includes(io.id));
        if (!use_in_object_arrays) {
            z3k_config = addHeatingCircuit(addHeatingCircuit(z3k_config, 1, [io.id]), 2, [io.id]);
        }
    });
    if (lodash.isEqual(z3k_config, input_z3k_config)) {
        return input_z3k_config;
    }
    return z3k_config;
};
const addHeatingCircuit = (config, counter, ids) => {
    const name = 'КО ' +
        (HeatingCircuit.get.where(config, HeatingCircuit.isConsumer).length + 1);
    const [newId, newConfig] = HeatingCircuit.get.add(config, {
        name,
        type: HeatingCircuit.Type.HEATING,
        water_min_temperature: 15,
        water_max_temperature: 65,
        air_temp_sensor: null,
        air_temp_sensor_reserve: null,
        dhw_temp_sensor: null,
        dhw_temp_sensor_reserve: null,
        water_temp_sensor: null,
        water_temp_sensor_reserve: null,
        hysteresis: 1,
        control_devices: [],
        setting_register: 65,
        turn_off_delay: 0,
        external_thermostat: null,
        heat_request: 2730,
        pza: 0.3,
        heat_source: null,
        antileg_mode_options: { m: 0, h: 0, days: 0 },
        summer_threshold: null,
        winter_summer_switch: false,
        locker_id: null,
        position: 0,
        delta_temp: 0,
    });
    return addPump(newConfig, { name, id: newId }, counter, [...ids, newId]);
};
const addPump = (config, heatingCircuit, counter, ids) => {
    const [newId, newConfig] = Pump.get.add(config, {
        name: 'Насос ' + heatingCircuit.name,
        // Гарантирую, что 0 элемент это id IOExtension.
        // Это в доке написано https://docs.google.com/document/d/183g5MOX2zXrsDLd5k5wmill7EMt0laDED_SoV3FICVI/edit#
        physical_output: composePhysicalIO(ids[0], counter === 1 ? 4 : 7),
        delay: 0,
        setting_register: 0,
        pressure_sensor: null,
        pressure_threshold: 0,
    });
    return addThreewayTap(newConfig, heatingCircuit, counter, [...ids, newId]);
};
const addThreewayTap = (config, heatingCircuit, counter, ids) => {
    let [newId, newConfig] = genIdAndIncCounter(config);
    const threewayTap = {
        name: 'Смеситель ' + heatingCircuit.name,
        physical_output1: (ids[0] << 4) | (counter === 1 ? 3 : 6), // Гарантирую, что 0 элемент это id IOExtension. Это в доке написано https://docs.google.com/document/d/183g5MOX2zXrsDLd5k5wmill7EMt0laDED_SoV3FICVI/edit#
        physical_output2: (ids[0] << 4) | (counter === 1 ? 2 : 5), // Гарантирую, что 0 элемент это id IOExtension. Это в доке написано https://docs.google.com/document/d/183g5MOX2zXrsDLd5k5wmill7EMt0laDED_SoV3FICVI/edit#
        step_time: 1,
        closing_time: 120,
        coefficient: 0,
        setting_register: 0,
        step_period: 10,
        id: newId,
    };
    newConfig = set(newConfig, 'threeway_taps', [
        ...(newConfig.threeway_taps ?? []),
        threewayTap,
    ]);
    ids.push(newId);
    return addAnalogTemperatureSensor(newConfig, heatingCircuit, counter, ids);
};
const addAnalogTemperatureSensor = (config, heatingCircuit, counter, ids) => {
    let [newId, newConfig] = genIdAndIncCounter(config);
    const analogTemperatureSensor = {
        physical_input_num: (ids[0] << 4) | (counter === 1 ? 0 : 1), // Гарантирую, что 0 элемент это id IOExtension. Это в доке написано https://docs.google.com/document/d/183g5MOX2zXrsDLd5k5wmill7EMt0laDED_SoV3FICVI/edit#
        name: 'tтн ' + heatingCircuit.name,
        type: 8,
        upper_threshold: null,
        lower_threshold: null,
        hysteresis: 0,
        no_connection_timeout: 1,
        rol_connection_lost: [20534],
        rol_temperature_upper_threshold: [20553],
        rol_temperature_lower_threshold: [20552],
        ntc: 20529,
        pull_up: 0,
        calibration_shift: 0,
        recovery_list: [],
        setting_register: 0,
        position: 0,
        id: newId,
    };
    newConfig = set(newConfig, 'analog_temperature_sensors', [
        ...(newConfig.analog_temperature_sensors ?? []),
        analogTemperatureSensor,
    ]);
    const intervals = [];
    ids.push(newId);
    return addInterval(newConfig, heatingCircuit, intervals, ids);
};
const addInterval = (config, heatingCircuit, intervals, ids) => {
    let [newId, newConfig] = genIdAndIncCounter(config);
    const timeInterval = {
        action_register: 0,
        eh: 0,
        em: 0,
        id: newId,
        sh: 0,
        sm: 0,
        temperature: heating_modes_ids.comfort,
    };
    intervals.push(timeInterval);
    ids.push(newId);
    newConfig = set(newConfig, 'time_intervals', [
        ...(newConfig.time_intervals ?? []),
        timeInterval,
    ]);
    if (intervals.length < 3) {
        return addInterval(newConfig, heatingCircuit, intervals, ids);
    }
    else {
        return addIntervalTimetables(newConfig, heatingCircuit, intervals, ids);
    }
};
const addIntervalTimetables = (config, heatingCircuit, intervals, ids) => {
    let [newId, newConfig] = genIdAndIncCounter(config);
    const intervalTimetable = {
        id: newId,
        outside_default: heating_modes_ids.economy,
        setting_register: 0,
        time_intervals: intervals.map((int) => int.id),
    };
    newConfig = set(newConfig, 'interval_timetables', [
        ...(newConfig.interval_timetables ?? []),
        intervalTimetable,
    ]);
    ids.push(newId);
    return addZoneInModes(newConfig, heatingCircuit, intervalTimetable, ids);
};
const addZoneInModes = (config, heatingCircuit, intervalTimetable, ids) => {
    if (config.heating_modes === undefined)
        return addObjectArray(config, ids);
    let newConfig = { ...config };
    config.heating_modes.forEach((mode, index) => {
        if (mode.id === heating_modes_ids.comfort) {
            newConfig = setIn(newConfig, ['heating_modes', index, 'heating_zones'], [
                ...(mode.heating_zones ?? []),
                {
                    heating_circuit: heatingCircuit.id,
                    temperature_setting: 2970,
                    adjusting_sensor: null,
                    pza: null,
                },
            ]);
        }
        else if (mode.id === heating_modes_ids.economy) {
            newConfig = setIn(newConfig, ['heating_modes', index, 'heating_zones'], [
                ...(mode.heating_zones ?? []),
                {
                    heating_circuit: heatingCircuit.id,
                    temperature_setting: 2910,
                    adjusting_sensor: null,
                    pza: null,
                },
            ]);
        }
        else if (mode.id === heating_modes_ids.off) {
            newConfig = setIn(newConfig, ['heating_modes', index, 'heating_zones'], [
                ...(mode.heating_zones ?? []),
                {
                    heating_circuit: heatingCircuit.id,
                    temperature_setting: 0,
                    adjusting_sensor: null,
                    pza: null,
                },
            ]);
        }
        else if (mode.id === heating_modes_ids.timetable) {
            newConfig = setIn(newConfig, ['heating_modes', index, 'heating_zones'], [
                ...(mode.heating_zones ?? []),
                {
                    heating_circuit: heatingCircuit.id,
                    temperature_setting: intervalTimetable.id,
                    adjusting_sensor: null,
                    pza: null,
                },
            ]);
        }
    });
    return addObjectArray(newConfig, ids);
};
const addObjectArray = (config, ids) => {
    let [newId, newConfig] = genIdAndIncCounter(config);
    const objectArray = {
        id: newId,
        objects_links: ids,
        setting_register: 0,
    };
    newConfig = set(newConfig, 'object_arrays', [
        ...(newConfig.object_arrays ?? []),
        objectArray,
    ]);
    return updateRelatedList(newConfig, ids, newId);
};
const updateRelatedList = (config, ids, objectArrayId) => {
    let newConfig = { ...config };
    newConfig.io_extensions?.forEach((io, index) => {
        if (io.id === ids[0]) {
            newConfig = setIn(newConfig, ['io_extensions', index, 'related_list'], [...(io.related_list ?? []), objectArrayId]);
        }
    });
    return newConfig;
};
